import React, { useState, useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { 
  PlusIcon, 
  ClockIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline'

interface Ticket {
  id: string
  title: string
  description: string
  category: 'billing' | 'technical' | 'provisioning' | 'admin_access'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  status: 'open' | 'in_progress' | 'waiting_customer' | 'resolved' | 'closed'
  createdAt: string
  updatedAt: string
  slaDueAt?: string
  assignedTo?: string
}

const Tickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'open' | 'resolved'>('all')

  useEffect(() => {
    fetchTickets()
  }, [])

  const fetchTickets = async () => {
    try {
      // Mock data for now - will be replaced with real API calls
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      const mockTickets: Ticket[] = [
        {
          id: 'ticket_001',
          title: 'Need additional Microsoft 365 licenses',
          description: 'We need to add 5 more Business Premium licenses for new hires starting next week.',
          category: 'provisioning',
          priority: 'medium',
          status: 'open',
          createdAt: '2025-01-15T10:30:00Z',
          updatedAt: '2025-01-15T10:30:00Z',
          slaDueAt: '2025-01-15T18:30:00Z'
        },
        {
          id: 'ticket_002',
          title: 'Question about billing cycle',
          description: 'Can you clarify when our annual renewal is due? We want to plan our budget accordingly.',
          category: 'billing',
          priority: 'low',
          status: 'resolved',
          createdAt: '2025-01-14T14:20:00Z',
          updatedAt: '2025-01-15T09:15:00Z',
          assignedTo: 'VCNS Support Team'
        },
        {
          id: 'ticket_003',
          title: 'Cannot access SharePoint Online',
          description: 'Getting error message when trying to access company SharePoint site. Error: Access Denied.',
          category: 'technical',
          priority: 'high',
          status: 'in_progress',
          createdAt: '2025-01-13T11:45:00Z',
          updatedAt: '2025-01-15T08:30:00Z',
          slaDueAt: '2025-01-15T15:45:00Z',
          assignedTo: 'Technical Support'
        },
        {
          id: 'ticket_004',
          title: 'Request GDAP permissions for admin tasks',
          description: 'Need delegated admin permissions to manage user accounts and security settings.',
          category: 'admin_access',
          priority: 'medium',
          status: 'waiting_customer',
          createdAt: '2025-01-12T16:10:00Z',
          updatedAt: '2025-01-14T12:00:00Z',
          assignedTo: 'Security Team'
        }
      ]
      
      setTickets(mockTickets)
    } catch (error) {
      console.error('Failed to fetch tickets:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getStatusBadge = (status: string) => {
    const badges = {
      open: { class: 'status-pending', label: 'Open' },
      in_progress: { class: 'status-badge bg-blue-100 text-blue-800', label: 'In Progress' },
      waiting_customer: { class: 'status-badge bg-yellow-100 text-yellow-800', label: 'Waiting' },
      resolved: { class: 'status-active', label: 'Resolved' },
      closed: { class: 'status-inactive', label: 'Closed' }
    }
    const badge = badges[status as keyof typeof badges] || badges.open
    return { class: badge.class, label: badge.label }
  }

  const getPriorityBadge = (priority: string) => {
    const badges = {
      low: 'priority-low',
      medium: 'priority-medium',
      high: 'priority-high',
      urgent: 'priority-urgent'
    }
    return badges[priority as keyof typeof badges] || badges.medium
  }

  const getCategoryIcon = (category: string) => {
    const icons = {
      billing: '💰',
      technical: '🔧',
      provisioning: '📦',
      admin_access: '🔐'
    }
    return icons[category as keyof typeof icons] || '📋'
  }

  const filteredTickets = tickets.filter(ticket => {
    if (filter === 'open') return ['open', 'in_progress', 'waiting_customer'].includes(ticket.status)
    if (filter === 'resolved') return ['resolved', 'closed'].includes(ticket.status)
    return true
  })

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-vcns-accent"></div>
      </div>
    )
  }

  return (
    <>
      <Helmet>
        <title>Support Tickets - VCNS Support Portal</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Support Tickets</h1>
            <p className="mt-1 text-sm text-gray-500">
              Track and manage your support requests
            </p>
          </div>
          <div className="mt-4 sm:mt-0">
            <Link to="/tickets/new" className="btn-primary">
              <PlusIcon className="w-4 h-4 mr-2" />
              Create Ticket
            </Link>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex space-x-4">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-vcns-accent bg-opacity-10 text-vcns-secondary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                All ({tickets.length})
              </button>
              <button
                onClick={() => setFilter('open')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  filter === 'open' 
                    ? 'bg-vcns-accent bg-opacity-10 text-vcns-secondary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Open ({tickets.filter(t => ['open', 'in_progress', 'waiting_customer'].includes(t.status)).length})
              </button>
              <button
                onClick={() => setFilter('resolved')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  filter === 'resolved' 
                    ? 'bg-vcns-accent bg-opacity-10 text-vcns-secondary' 
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                Resolved ({tickets.filter(t => ['resolved', 'closed'].includes(t.status)).length})
              </button>
            </div>
          </div>
        </div>

        {/* Tickets List */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {filteredTickets.length === 0 ? (
            <div className="px-6 py-12 text-center">
              <ChatBubbleLeftRightIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No tickets found</h3>
              <p className="text-gray-500 mb-4">
                {filter === 'all' 
                  ? "You haven't created any support tickets yet."
                  : `No tickets match the ${filter} filter.`
                }
              </p>
              <Link to="/tickets/new" className="btn-primary">
                Create Your First Ticket
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredTickets.map((ticket) => {
                const statusBadge = getStatusBadge(ticket.status)
                const isOverdue = ticket.slaDueAt && new Date(ticket.slaDueAt) < new Date()
                
                return (
                  <Link
                    key={ticket.id}
                    to={`/tickets/${ticket.id}`}
                    className="block px-6 py-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-lg">{getCategoryIcon(ticket.category)}</span>
                          <h3 className="text-lg font-medium text-gray-900 truncate">
                            {ticket.title}
                          </h3>
                          <span className={statusBadge.class}>
                            {statusBadge.label}
                          </span>
                          <span className={`${getPriorityBadge(ticket.priority)} capitalize`}>
                            {ticket.priority}
                          </span>
                        </div>
                        
                        <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                          {ticket.description}
                        </p>
                        
                        <div className="flex items-center space-x-4 text-xs text-gray-500">
                          <span>#{ticket.id.split('_')[1]}</span>
                          <span>Created {formatDate(ticket.createdAt)}</span>
                          <span>Updated {formatDate(ticket.updatedAt)}</span>
                          {ticket.assignedTo && (
                            <span>Assigned to {ticket.assignedTo}</span>
                          )}
                          {ticket.slaDueAt && (
                            <span className={`flex items-center ${isOverdue ? 'text-red-600' : ''}`}>
                              <ClockIcon className="w-3 h-3 mr-1" />
                              SLA {isOverdue ? 'overdue' : `due ${formatDate(ticket.slaDueAt)}`}
                            </span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        {isOverdue && (
                          <ExclamationTriangleIcon className="w-5 h-5 text-red-500" />
                        )}
                        {ticket.status === 'resolved' && (
                          <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        )}
                      </div>
                    </div>
                  </Link>
                )
              })}
            </div>
          )}
        </div>

        {/* Quick Stats */}
        {filteredTickets.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-blue-600">
                {tickets.filter(t => t.status === 'open').length}
              </div>
              <div className="text-sm text-gray-500">Open</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {tickets.filter(t => t.status === 'in_progress').length}
              </div>
              <div className="text-sm text-gray-500">In Progress</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-red-600">
                {tickets.filter(t => t.priority === 'urgent' && ['open', 'in_progress'].includes(t.status)).length}
              </div>
              <div className="text-sm text-gray-500">Urgent</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow text-center">
              <div className="text-2xl font-bold text-green-600">
                {tickets.filter(t => t.status === 'resolved').length}
              </div>
              <div className="text-sm text-gray-500">Resolved</div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default Tickets
